import { ReactNode } from "react";
import { InlineMath } from "react-katex";
import { constructInputWithSpecialExpressionList } from "../../../utilization/CatchtivityUtilization";
import { IInputPart } from "../../../properties/ActivityProperties";
import ShowBodyMediaByContentType from "./ShowBodyMediaByContentType";

// Renders a string (with LaTeX / bold / underline markers) as inline spans — safe to
// drop inside a sentence. Reused by the Dropdown / FillInTheBlanks blank renderers too.
export const renderInlineParts = (value: string): ReactNode =>
  constructInputWithSpecialExpressionList(value || "").map(
    (part: IInputPart, i: number) => (
      <span
        key={i}
        className={`${part.isBold ? "mc-bold" : ""} ${
          part.isUnderline ? "mc-underline" : ""
        }`}
      >
        {part.isEquation ? (
          <span className="mc-xl">
            <InlineMath math={part.value} />
          </span>
        ) : (
          part.value
        )}
      </span>
    ),
  );

interface IInlineSentenceBodyProps {
  bodyMap: any;
  // Renders the live control for the n-th blank (0-based, spanning all body entries).
  renderBlank: (blankIndex: number) => ReactNode;
  // Total number of blanks (= materialMap keys). Any blank not consumed by a `@@`
  // placeholder in the body is rendered as a numbered fallback row below the sentence,
  // so blanks are never lost when the body has fewer/no `@@` markers.
  totalBlanks: number;
}

// Renders the question body for the "inline" Dropdown / FillInTheBlanks variant: each
// `@@` placeholder becomes the live control returned by renderBlank, sitting in the
// sentence. Non-text body entries (a stray IMAGE/VIDEO/AUDIO stem) fall back to the
// shared media renderer. Blank ordering follows body-map insertion order (no sort).
const InlineSentenceBody = ({
  bodyMap,
  renderBlank,
  totalBlanks,
}: IInlineSentenceBodyProps) => {
  if (!bodyMap) return null;
  let blankIndex = 0;

  const keys = Object.keys(bodyMap);
  const parsedEntries = keys.map((key) => {
    try {
      return JSON.parse(bodyMap[key]);
    } catch (error) {
      return null;
    }
  });
  // A body image floats (newspaper column) only when a text entry follows it, so
  // there's a sentence to wrap beside it; a trailing image stays a full-width block.
  const hasTextAfter = (entryIndex: number) =>
    parsedEntries
      .slice(entryIndex + 1)
      .some((entry: any) => entry && (!entry.type || entry.type === "TEXT"));

  const lines = keys.map((key, entryIndex) => {
    const body: any = parsedEntries[entryIndex];
    if (body == null) return null;

    if (body?.type && body.type !== "TEXT") {
      return (
        <ShowBodyMediaByContentType
          key={`m-${key}`}
          index={entryIndex}
          type={body.type}
          value={body.value}
          size={body.size}
          floatMode="any"
          canFloat={hasTextAfter(entryIndex)}
        />
      );
    }

    const segments = String(body?.value ?? "").split("@@");
    const nodes: ReactNode[] = [];
    segments.forEach((segment, i) => {
      if (segment !== "") {
        nodes.push(<span key={`t-${key}-${i}`}>{renderInlineParts(segment)}</span>);
      }
      if (i < segments.length - 1) {
        const currentBlankIndex = blankIndex;
        blankIndex += 1;
        nodes.push(
          <span key={`b-${key}-${i}`} className="aas-blank-wrap">
            {renderBlank(currentBlankIndex)}
          </span>,
        );
      }
    });

    return (
      <p key={`l-${key}`} className="aas-inline-line">
        {nodes}
      </p>
    );
  });

  // Blanks the body's `@@` markers didn't account for (incl. the common case of a body
  // with no `@@` at all) are rendered as numbered rows so every blank stays answerable.
  const fallbackBlanks: ReactNode[] = [];
  for (let i = blankIndex; i < totalBlanks; i += 1) {
    fallbackBlanks.push(
      <div key={`fb-${i}`} className="aas-blank-row">
        <span className="aas-blank-num">{i + 1}.</span>
        {renderBlank(i)}
      </div>,
    );
  }

  return (
    <div className="aas-inline-sentence">
      {lines}
      {fallbackBlanks.length > 0 ? (
        <div className="aas-blank-list">{fallbackBlanks}</div>
      ) : null}
    </div>
  );
};

export default InlineSentenceBody;
